/** Counter ops (spec §8.1 `counters`): `{title, unit, entries: {YYYY-MM-DD: number}}`; a per-person habit keeps
 *  each profile's values in `entriesBy.<profileId>` instead (ADR 0024). */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { ulid } from "ulid";
import { actorProfile, commitMessage } from "../../core/actor";
import { C } from "../../core/schema";

export type CounterKind = "count" | "check";
export type CounterPeriod = "day" | "week";
/** `shared`: one value per day for the workspace (the default); `perPerson`: each person ticks for themselves */
export type CounterScope = "shared" | "perPerson";

/** A counter, or a habit (spec §9.7 `habits`): `check` = done or not per day, `count` = a number per day,
 *  with an optional goal per day or week. */
export interface Counter {
  title: string;
  unit?: string;
  kind?: CounterKind;
  goal?: number;
  period?: CounterPeriod;
  /** a habit (habits widget, *Interact with a habit*) or a plain counter (counters widget); schema v7 */
  habit?: boolean;
  archivedAt?: string;
  entries: Record<string, number>;
  scope?: CounterScope;
  entriesBy?: Record<string, Record<string, number>>;
  /** linked into several workspaces (ADR 0024): where it should be; the server keeps the copies */
  linkWorkspaces?: string;
}

/** The values that count for `me`: the workspace's, or for a per-person habit only `me`'s own (others' are never
 *  shown, spec §9.29). */
export function valuesOf(c: Counter, me: string | null): Record<string, number> {
  if (c.scope !== "perPerson") return c.entries ?? {};
  return (me && c.entriesBy?.[me]) || {};
}

/** Where a write goes: the shared values, or the acting person's own. */
function valuesFor(m: LoroMap): LoroMap {
  if (m.get("scope") !== "perPerson") {
    const entries = m.get("entries") as LoroMap | undefined;
    return entries ?? m.setContainer("entries", new LoroMap());
  }
  const me = actorProfile();
  if (!me) throw new Error("counters.whoRequired");
  let by = m.get("entriesBy") as LoroMap | undefined;
  if (!by) by = m.setContainer("entriesBy", new LoroMap());
  const mine = by.get(me) as LoroMap | undefined;
  return mine ?? by.setContainer(me, new LoroMap());
}

/**
 * Where a counter does not say (made before schema v7 or by an older client): yes/no, with a goal or weekly is a
 * habit, a plain daily number a counter. Same rule as `counter_is_habit` in `server/drang/core/ops.py`.
 */
export function counterIsHabit(kind?: CounterKind, goal?: number, period?: CounterPeriod): boolean {
  return kind === "check" || !!goal || period === "week";
}

/** Whether a counter shows among the habits (true) or the counters (false). */
export function isHabit(c: Counter): boolean {
  return typeof c.habit === "boolean" ? c.habit : counterIsHabit(c.kind, c.goal, c.period);
}

export function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function createCounter(
  doc: LoroDoc,
  title: string,
  unit = "",
  opts: {
    kind?: CounterKind;
    goal?: number;
    period?: CounterPeriod;
    habit?: boolean;
    scope?: CounterScope;
  } = {},
): string {
  const id = ulid();
  const m = doc.getMap(C.counters).setContainer(id, new LoroMap());
  m.set("title", title);
  m.set("unit", unit);
  m.set("kind", opts.kind ?? "count");
  m.set("period", opts.period ?? "day");
  if (opts.goal) m.set("goal", opts.goal);
  m.set("habit", opts.habit ?? counterIsHabit(opts.kind, opts.goal, opts.period));
  if (opts.scope === "perPerson") m.set("scope", "perPerson");
  m.setContainer("entries", new LoroMap());
  doc.commit({ message: commitMessage({ op: "createCounter" }) });
  return id;
}

export function bumpCounter(doc: LoroDoc, id: string, delta: number, day = new Date()): void {
  const m = doc.getMap(C.counters).get(id) as LoroMap | undefined;
  if (!m) throw new Error("counters.noSuchCounter");
  const entries = valuesFor(m);
  const key = dayKey(day);
  const cur = (entries.get(key) as number | undefined) ?? 0;
  entries.set(key, Math.max(0, cur + delta));
  doc.commit({ message: commitMessage({ op: "bumpCounter" }) });
}

export function deleteCounter(doc: LoroDoc, id: string): void {
  doc.getMap(C.counters).delete(id);
  doc.commit({ message: commitMessage({ op: "deleteCounter" }) });
}

function counterMap(doc: LoroDoc, id: string): LoroMap {
  const m = doc.getMap(C.counters).get(id) as LoroMap | undefined;
  if (!m) throw new Error("counters.noSuchCounter");
  return m;
}

/** One day's value: a habit ticked (1) or unticked (0), or a correction. 0 removes the day. */
export function setCounterValue(doc: LoroDoc, id: string, day: string, value: number): void {
  const entries = valuesFor(counterMap(doc, id));
  const v = Math.max(0, value);
  if (v === 0) entries.delete(day);
  else entries.set(day, v);
  doc.commit({ message: commitMessage({ op: "setCounterValue" }) });
}

/** Change a counter or habit; `goal: 0` removes the goal; `archived` retires it (history kept). */
export function updateCounter(
  doc: LoroDoc,
  id: string,
  patch: {
    title?: string;
    unit?: string;
    kind?: CounterKind;
    goal?: number;
    period?: CounterPeriod;
    archived?: boolean;
    /** move it to the habits (true) or the counters (false) */
    habit?: boolean;
    /** values for the workspace or per person; values already ticked stay where they are */
    scope?: CounterScope;
  },
): void {
  const m = counterMap(doc, id);
  if (patch.title !== undefined) m.set("title", patch.title);
  if (patch.unit !== undefined) m.set("unit", patch.unit);
  if (patch.kind !== undefined) m.set("kind", patch.kind);
  if (patch.period !== undefined) m.set("period", patch.period);
  if (patch.goal !== undefined) {
    if (patch.goal > 0) m.set("goal", patch.goal);
    else m.delete("goal");
  }
  if (patch.habit !== undefined) m.set("habit", patch.habit);
  if (patch.scope !== undefined) m.set("scope", patch.scope);
  if (patch.archived === true) m.set("archivedAt", new Date().toISOString());
  if (patch.archived === false) m.delete("archivedAt");
  doc.commit({ message: commitMessage({ op: "updateCounter" }) });
}
